import { useEffect, useState } from 'react';
import { companyAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button, Table, Modal, Input, Select, Loading, Alert } from './ui';

const empty = { name:'', short_name:'', address:'', phone:'', email:'',
                tin_no:'', vat_no:'', br_no:'', status:'active' };

export default function Companies() {
  const { can } = useAuth();
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [success, setSuccess] = useState('');
  const [modal,   setModal]   = useState(false);
  const [editing, setEditing] = useState(null);
  const [form,    setForm]    = useState(empty);
  const [saving,  setSaving]  = useState(false);

  const flash = (m) => { setSuccess(m); setTimeout(() => setSuccess(''), 3000); };

  const load = () => {
    setLoading(true);
    companyAPI.getAll()
      .then(r => setItems(r.data.data || []))
      .catch(() => setError('Failed to load companies'))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openAdd  = () => { setEditing(null); setForm(empty); setModal(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...empty, ...c }); setModal(true); };

  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (editing) await companyAPI.update(editing.id, form);
      else         await companyAPI.create(form);
      flash(editing ? 'Company updated' : 'Company added');
      setModal(false); load();
    } catch (err) { setError(err.response?.data?.message || 'Failed to save company'); }
    finally { setSaving(false); }
  };

  const toggle = async (c) => {
    try { await companyAPI.update(c.id, { status: c.status === 'active' ? 'inactive' : 'active' }); load(); }
    catch { setError('Failed to update status'); }
  };

  return (
    <div className="space-y-4">
      {error   && <Alert type="error"   message={error} />}
      {success && <Alert type="success" message={success} />}

      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">
          Companies that own vehicles — these appear in the vehicle form's <strong>Owner Company</strong> list.
        </p>
        {can('admin','transport_officer') && <Button onClick={openAdd}>+ Add Company</Button>}
      </div>

      {loading ? <Loading /> : (
        <Table headers={['Company','Short Name','Address','Contact','Tax / Reg No','Status','Actions']} empty="No companies yet">
          {items.map(c => (
            <tr key={c.id} className={`border-b last:border-0 hover:bg-gray-50 ${c.status === 'inactive' ? 'opacity-50' : ''}`}>
              <td className="px-4 py-3 text-sm font-medium">{c.name}</td>
              <td className="px-4 py-3 text-sm text-gray-600">{c.short_name || '—'}</td>
              <td className="px-4 py-3 text-sm text-gray-600 max-w-[220px] truncate" title={c.address}>{c.address || '—'}</td>
              <td className="px-4 py-3 text-sm text-gray-600">
                {c.phone || '—'}{c.email && <p className="text-xs text-gray-400">{c.email}</p>}
              </td>
              <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">
                {(c.tin_no || c.vat_no || c.br_no) ? (
                  <>
                    {c.tin_no && <p><span className="text-gray-400">TIN</span> {c.tin_no}</p>}
                    {c.vat_no && <p><span className="text-gray-400">VAT</span> {c.vat_no}</p>}
                    {c.br_no  && <p><span className="text-gray-400">BR</span> {c.br_no}</p>}
                  </>
                ) : <span className="text-sm">—</span>}
              </td>
              <td className="px-4 py-3">
                <span className={`px-2 py-1 rounded-full text-xs ${c.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                  {c.status}
                </span>
              </td>
              <td className="px-4 py-3">
                {can('admin','transport_officer') && (
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" title="Edit" onClick={() => openEdit(c)}>✏️</Button>
                    <Button variant="ghost" size="sm" title={c.status === 'active' ? 'Deactivate' : 'Activate'}
                      onClick={() => toggle(c)}>{c.status === 'active' ? '🚫' : '✅'}</Button>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </Table>
      )}

      <Modal open={modal} onClose={() => setModal(false)}
        title={editing ? `Edit — ${editing.name}` : 'Add Company'}>
        <form onSubmit={save} className="space-y-4">
          <Input label="Company Name *" required value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })}
            placeholder="e.g. Hasthiya Investments (Pvt) Ltd" />
          <div className="grid grid-cols-2 gap-4">
            <Input label="Short Name" value={form.short_name}
              onChange={e => setForm({ ...form, short_name: e.target.value })} placeholder="e.g. HIT" />
            <Select label="Status" value={form.status}
              onChange={e => setForm({ ...form, status: e.target.value })}>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </Select>
          </div>
          <Input label="Address" value={form.address}
            onChange={e => setForm({ ...form, address: e.target.value })} />
          <div className="grid grid-cols-2 gap-4">
            <Input label="Phone" value={form.phone}
              onChange={e => setForm({ ...form, phone: e.target.value })} />
            <Input label="Email" type="email" value={form.email}
              onChange={e => setForm({ ...form, email: e.target.value })} />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <Input label="TIN No" value={form.tin_no}
              onChange={e => setForm({ ...form, tin_no: e.target.value })} placeholder="e.g. 134567890-7000" />
            <Input label="VAT No" value={form.vat_no}
              onChange={e => setForm({ ...form, vat_no: e.target.value })} placeholder="e.g. 134567890-7000" />
            <Input label="BR No" value={form.br_no}
              onChange={e => setForm({ ...form, br_no: e.target.value })} placeholder="e.g. PV 12345" />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => setModal(false)}>Cancel</Button>
            <Button type="submit" disabled={saving}>{saving ? 'Saving…' : editing ? 'Save Changes' : 'Add Company'}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
